import React, { useState, useEffect } from 'react';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Select } from '../components/common/Select';
import { Textarea } from '../components/common/Textarea';
import { Card } from '../components/common/Card';
import { Alert } from '../components/common/Alert';
import Modal from '../components/common/Modal';
import api from '../services/api';
import { useNotification } from '../contexts/NotificationContext';
import type { LeaveType } from '../types';

interface HourlyLeaveEntry {
  id: number;
  user_id: number;
  leave_type_id: number;
  date: string;
  hours: number;
  description?: string;
  status: 'pending' | 'auto_converted' | 'cancelled';
  leave_request_id?: number;
  accumulated_hours: number;
  created_at: string;
  updated_at: string;
  leaveType?: LeaveType;
  leaveRequest?: any;
}

interface HourlyLeaveBalance {
  leave_type_id: number;
  leave_type: LeaveType;
  total_hours: number;
  entries_count: number;
  hours_to_full_day: number;
}

const HourlyLeaveEntryPage: React.FC = () => {
  const [entries, setEntries] = useState<HourlyLeaveEntry[]>([]);
  const [balances, setBalances] = useState<HourlyLeaveBalance[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState<HourlyLeaveEntry | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  const [formData, setFormData] = useState({
    leave_type_id: '',
    date: '',
    hours: '',
    description: ''
  });

  const { showNotification } = useNotification();

  useEffect(() => {
    loadData();
    loadLeaveTypes();
  }, [currentPage]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [entriesResponse, balanceResponse] = await Promise.all([
        api.get(`/hourly-leaves/my?page=${currentPage}&limit=10`),
        api.get('/hourly-leaves/my/balance')
      ]);

      setEntries(entriesResponse.data.data.entries);
      setTotalPages(entriesResponse.data.data.pagination.totalPages);
      setBalances(balanceResponse.data.data);
    } catch (error) {
      console.error('Error loading hourly leave data:', error);
      showNotification({ type: 'error', title: 'Błąd podczas ładowania danych' });
    } finally {
      setLoading(false);
    }
  };

  const loadLeaveTypes = async () => {
    try {
      console.log('Loading leave types with api:', api);
      const response = await api.get('/leave-types');
      console.log('Leave types response:', response.data);
      setLeaveTypes(response.data || []);
    } catch (error) {
      console.error('Error loading leave types:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const data = {
        leave_type_id: parseInt(formData.leave_type_id),
        date: formData.date,
        hours: parseFloat(formData.hours),
        description: formData.description
      };

      const response = await api.post('/hourly-leaves', data);
      
      if (response.data.autoConvertedRequest) {
        showNotification({
          type: 'success',
          title: 'Wpis został utworzony i automatycznie przekonwertowany',
          message: `Wniosek urlopowy nr ${response.data.autoConvertedRequest.id}`
        });
      } else {
        showNotification({ type: 'success', title: 'Wpis godzinowy został utworzony' });
      }
      
      setShowAddModal(false);
      resetForm();
      loadData();
    } catch (error: any) {
      showNotification({ type: 'error', title: error.response?.data?.message || 'Błąd podczas tworzenia wpisu' });
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingEntry) return;
    
    try {
      const data = {
        hours: parseFloat(formData.hours),
        description: formData.description
      };

      const response = await api.put(`/hourly-leaves/${editingEntry.id}`, data);
      
      if (response.data.autoConvertedRequest) {
        showNotification({
          type: 'success',
          title: 'Wpis został zaktualizowany i automatycznie przekonwertowany',
          message: `Wniosek urlopowy nr ${response.data.autoConvertedRequest.id}`
        });
      } else {
        showNotification({ type: 'success', title: 'Wpis został zaktualizowany' });
      }
      
      setEditingEntry(null);
      resetForm();
      loadData();
    } catch (error: any) {
      showNotification({ type: 'error', title: error.response?.data?.message || 'Błąd podczas edycji wpisu' });
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Czy na pewno chcesz anulować ten wpis?')) return;
    
    try {
      await api.delete(`/hourly-leaves/${id}`);
      showNotification({ type: 'success', title: 'Wpis został anulowany' });
      loadData();
    } catch (error: any) {
      showNotification({ type: 'error', title: error.response?.data?.message || 'Błąd podczas anulowania wpisu' });
    }
  };

  const resetForm = () => {
    setFormData({
      leave_type_id: '',
      date: '',
      hours: '',
      description: ''
    });
  };

  const openEditModal = (entry: HourlyLeaveEntry) => {
    setEditingEntry(entry);
    setFormData({
      leave_type_id: entry.leave_type_id.toString(),
      date: entry.date,
      hours: entry.hours.toString(),
      description: entry.description || ''
    });
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'auto_converted': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    
    const labels = {
      'pending': 'Oczekuje',
      'auto_converted': 'Przekonwertowane',
      'cancelled': 'Anulowane'
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[status as keyof typeof colors]}`}>
        {labels[status as keyof typeof labels]}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Godziny urlopowe</h1>
        <Button onClick={() => setShowAddModal(true)}>
          Dodaj wpis godzinowy
        </Button>
      </div>

      {balances.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold mb-4">Aktualne salda godzinowe</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {balances.map((balance) => (
              <div key={balance.leave_type_id} className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900">{balance.leave_type.name}</h4>
                <div className="mt-2 text-sm text-blue-700">
                  <div>Zgromadzone godziny: <span className="font-semibold">{balance.total_hours}h</span></div>
                  <div>Liczba wpisów: {balance.entries_count}</div>
                  <div>Do pełnego dnia: <span className="font-semibold">{balance.hours_to_full_day}h</span></div>
                  <div className="mt-2">
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${Math.min((balance.total_hours / 8) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <h3 className="text-lg font-semibold mb-4">Historia wpisów godzinowych</h3>
        
        {loading ? (
          <div className="text-center py-8">Ładowanie...</div>
        ) : entries.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Brak wpisów godzinowych
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Typ urlopu
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Godziny
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Działania
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {entries.map((entry) => (
                    <tr key={entry.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(entry.date).toLocaleDateString('pl-PL')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entry.leaveType?.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entry.hours}h
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(entry.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {entry.status === 'pending' && (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => openEditModal(entry)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              Edytuj
                            </button>
                            <button
                              onClick={() => handleDelete(entry.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Anuluj
                            </button>
                          </div>
                        )}
                        {entry.status === 'auto_converted' && entry.leave_request_id && (
                          <span className="text-green-600">
                            Wniosek #{entry.leave_request_id}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {totalPages > 1 && (
              <div className="mt-4 flex justify-center space-x-2">
                <Button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  variant="outline"
                >
                  Poprzednia
                </Button>
                <span className="flex items-center px-4">
                  Strona {currentPage} z {totalPages}
                </span>
                <Button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  variant="outline"
                >
                  Następna
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      <Modal
        isOpen={showAddModal || !!editingEntry}
        onClose={() => {
          setShowAddModal(false);
          setEditingEntry(null);
          resetForm();
        }}
        title={editingEntry ? "Edytuj wpis godzinowy" : "Dodaj wpis godzinowy"}
      >
        <form onSubmit={editingEntry ? handleEdit : handleSubmit} className="space-y-4">
          <Select
            label="Typ urlopu"
            value={formData.leave_type_id}
            onChange={(e) => setFormData({ ...formData, leave_type_id: e.target.value })}
            required
            disabled={!!editingEntry}
          >
            <option value="">Wybierz typ urlopu</option>
            {leaveTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </Select>

          <Input
            type="date"
            label="Data"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            required
            disabled={!!editingEntry}
          />

          <Input
            type="number"
            label="Liczba godzin"
            value={formData.hours}
            onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
            min="0.5"
            max="8"
            step="0.5"
            required
          />

          <Textarea
            label="Opis (opcjonalny)"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={3}
          />

          <Alert type="info">
            <p className="text-sm">
              Po zgromadzeniu 8 godzin dla danego typu urlopu, system automatycznie utworzy wniosek urlopowy na dzień ostatniego wpisu.
            </p>
          </Alert>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowAddModal(false);
                setEditingEntry(null);
                resetForm();
              }}
            >
              Anuluj
            </Button>
            <Button type="submit">
              {editingEntry ? 'Zaktualizuj' : 'Dodaj'} wpis
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default HourlyLeaveEntryPage;